import EasyPeasyLogo from "@/components/icons/EasyPeasyLogo";
import FullLogo from "@/components/icons/logo/Dark.tsx";
import LogoutIcon from "@/components/icons/LogoutIcon.tsx";
import NewNotificationIcon from "@/components/icons/NewNotificationIcon";
import { Button } from "@/components/ui/Button";
import Hamburger from "@/components/ui/Hamburger.tsx";
import ActiveLink from "@/components/ui/links/ActiveLink.tsx";
import routes from "@/config/routes";
import API_ENDPOINTS from "@/data/client/endpoints";
import {
  useLogout,
  useMe,
  useNotifications,
  useSetNotificationMetadataAsSeen,
  useUserProducts,
} from "@/data/user.ts";
import {
  MIXPANEL_EVENT_TYPES,
  MIXPANEL_PAGE_VERSION_MOBILE,
  getSidebarEventName,
  maixpanelTrackEventProps,
} from "@/mixpanel/MixpanelUtils";
import useRouteObjects from "@/routes/RoutesObject.tsx";
import {
  LoginUserItem,
  ProductItem,
  SortedProducts,
  UserNotificationItem,
} from "@/types";
import useGlobals from "@/utils/hooks/useGlobals.ts";
import {
  filterNotificationsExercises,
  filterNotificationsMaterials,
  filterUnseenNotificationsEvents,
} from "@/utils/notifications";
import userCreditsSummary from "@/utils/user-credits-summary";
import { cn } from "@edusogno/lib/utils";
import { ScrollArea } from "@edusogno/primitives/scroll-area";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useToggle } from "react-use";

export default function DashboardSideBar() {
  const navigate = useNavigate();
  const { me } = useMe();
  const { menuItems } = useRouteObjects();
  const { notifications } = useNotifications();
  const { mutate: logout } = useLogout();
  const { sidebarContent } = useGlobals();
  const lang = localStorage.getItem("i18nextLng");
  const [menuItemsToHide, setMenuItemsToHide] = useState<string[]>([]);
  const isItalian = ["it"].includes(lang as string);
  const queryClient = useQueryClient();
  const { mutateAsync } = useSetNotificationMetadataAsSeen();
  const { products } = useUserProducts();

  const [isToggle, toggle] = useToggle(false);
  const [hasNewNotifications, setHasNewNotifications] = useState(() =>
    filterUnseenNotificationsEvents(notifications?.items || []),
  );
  const [selectedProduct, setSelectedProduct] = useState<string>();
  const [isDiplomaRequestUnlock, setIsDiplomaRequestUnlock] = useState(false);

  const totalPayments = useMemo(() => {
    if (me) {
      return me.item.user.payments
        .map((p) => parseInt(p.amount))
        .reduce((a, b) => a + b, 0);
    }
    return 0;
  }, [me]);
  const isItalianNoDeposit = me?.item.user.lang == "it" && totalPayments < 120;

  useEffect(() => {
    const newNotifications = filterUnseenNotificationsEvents(
      notifications?.items || [],
    );
    setHasNewNotifications(newNotifications);
  }, [notifications]);

  const handleHideNewNotificationIcon = async (value: string) => {
    if (value === "Home" && hasNewNotifications) {
      if (me) {
        await mutateAsync({ token_user: me.item.user.token });
        queryClient.setQueryData<UserNotificationItem[]>(
          [API_ENDPOINTS.NOTIFICATIONS_SET_SEEN],
          (old) =>
            old?.map((n) =>
              n.token === me.item.user.token
                ? {
                    ...n,
                    seen: true,
                  }
                : n,
            ),
        );
      }
    }
  };

  const handleHomeClick = (value: string) => {
    setHasNewNotifications(false);
    handleHideNewNotificationIcon(value)
      .then(() => {})
      .catch((error) => {
        console.error(error);
      });
  };

  const hasNewMaterials = useMemo(() => {
    return filterNotificationsMaterials(notifications?.items || []);
  }, [notifications]);

  const hasNewExercises = useMemo(() => {
    return filterNotificationsExercises(notifications?.items || []);
  }, [notifications]);

  const location = useLocation();
  const pathList = location.pathname.split("/");
  const pageTag = pathList[pathList.length - 1];

  const listOfBackButtonPages = ["event", "book-speaking-class"];

  // const [classReport, setClassReport] = useState<UserNotificationItem[]>();
  const [isIeltsCourse, setIsIeltsCourse] = useState<boolean>(false);

  useEffect(() => {
    if (!me?.item.user) return;

    const userProducts = me.item.user.products ? me.item.user.products : [];

    userProducts.forEach((product) => {
      product?.course_info?.course_structure?.forEach((element) => {
        if (element.ielts > 0) {
          setIsIeltsCourse(true);
        }
      });
    });
    const creditSummary = userCreditsSummary(userProducts);
    if (creditSummary.isOneToOneOnly) {
      const newHiddenItemList = menuItemsToHide;
      newHiddenItemList.push("Availability");
      setMenuItemsToHide(newHiddenItemList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.item.user]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    if (typeof window.Intercom === "function") {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      window.Intercom("update", {
        hide_default_launcher: !isToggle,
        alignment: "right",
      });
    }
  }, [isToggle]);

  const mixpanelPropsData = JSON.stringify(
    maixpanelTrackEventProps(
      "Dashboard Sidebar Menu",
      me?.item.user as LoginUserItem,
    ),
  );

  useEffect(() => {
    if (products && products.items) {
      const groupedProducts = products.items.reduce(
        (acc: { [key: string]: ProductItem[] }, product) => {
          const productCourseName: string = product?.course_info?.course_name;
          if (!acc[productCourseName]) {
            acc[productCourseName] = [];
          }
          acc[productCourseName].push(product);
          return acc;
        },
        {},
      );

      const selectOptions = Object.values(groupedProducts).flatMap((group) => {
        const sortedGroup = group.sort((a: ProductItem, b: ProductItem) => {
          const startDateA = a.pivot?.start_date;
          const startDateB = b.pivot?.start_date;
          if (startDateA && startDateB) {
            return (
              new Date(startDateB).getTime() - new Date(startDateA).getTime()
            );
          } else {
            return 0;
          }
        });

        return sortedGroup.map((product: ProductItem, index) => ({
          label: product?.course_info?.course_name,
          value: index === 0 ? product.id : `${product.id}_1`,
          isNew: index === 0,
          startDate: product?.pivot?.start_date,
          ...product,
        }));
      }) as SortedProducts[];

      const productSelected = selectOptions.find((option) => option.isNew);
      const selectedProductString = JSON.stringify(productSelected);
      setSelectedProduct(selectedProductString);
    }
  }, [products]);

  useEffect(() => {
    if (me && products && products.items) {
      // const upselling = Boolean(user?.crm_upselling);

      const currentProduct = selectedProduct
        ? (JSON.parse(selectedProduct) as ProductItem)
        : null;

      const excludePaths =
        !currentProduct?.product_tag
          ?.toLowerCase()
          .includes("lezioni individuali") ||
        !currentProduct?.product_tag?.toLowerCase().includes("ielts") ||
        false;

      if (currentProduct) {
        const endDateCourse = currentProduct?.pivot?.end_date;

        if (endDateCourse) {
          const subDays = dayjs(endDateCourse).subtract(21, "d");
          const now = dayjs();

          setIsDiplomaRequestUnlock(subDays <= now || !excludePaths);
        }
      }
    }
  }, [products, me, selectedProduct]);

  const BackIcon = () => (
    <svg
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      width="15"
      height="15"
      viewBox="0 0 17 21"
    >
      <path
        d="M12 2L3 10.7931L12 19"
        stroke="#2D224C"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );

  return (
    <div
      className={cn(
        "md:flex until-md:fixed md:relative z-40 !pointer-events-auto fixed until-md:w-full w-[255px]",
        { "h-full": isToggle },
      )}
    >
      {/*Mobile Nav*/}
      <nav
        className={cn(
          "hidden until-md:block bg-light relative w-full top-0 rounded-b-lg drop-shadow shadow-lg shadow-brand-secondary transition-all ease-in-out duration-200",
          { "h-full": isToggle, "h-24": !isToggle },
        )}
      >
        <div className="px-[--dashboard-sidebar] h-full @container/sidebar">
          <div className="relative flex h-24 justify-between items-center">
            <div className="flex flex-1">
              <div className="flex flex-shrink-0 items-center">
                <Link to={routes.home}>
                  {!isItalian ? (
                    <EasyPeasyLogo width={96} color="#2D224C" />
                  ) : (
                    <FullLogo width={96} className="fill-brand" />
                  )}
                </Link>
              </div>
            </div>

            <div>
              {listOfBackButtonPages.includes(pageTag) ? (
                <button
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  <BackIcon />
                </button>
              ) : (
                <button onClick={toggle}>
                  <Hamburger isToggle={isToggle} />
                </button>
              )}
            </div>
          </div>

          <ScrollArea className="h-[calc(100%_-_150px)]">
            <nav
              className={cn(
                "mt-5 flex-1 space-y-1 px-2 transition-opacity duration-300 opacity-0 hidden",
                { "opacity-100 flex flex-col": isToggle },
              )}
            >
              {menuItems.map((item) =>
                (me?.item.user.english_level == "A0" &&
                  item.value == "1:1 classes") ||
                (!isIeltsCourse && item.value == "Book exam") ||
                (item.value == "Availability" &&
                  menuItemsToHide.includes(item.value)) ||
                (item.value == "Availability" && isItalianNoDeposit) ? null : (
                  <ActiveLink
                    data-track-props={mixpanelPropsData}
                    data-track-eventtype={getSidebarEventName(item.value)}
                    data-track-pageversion={MIXPANEL_PAGE_VERSION_MOBILE}
                    key={item.value}
                    to={item.path}
                    onClick={() => {
                      toggle(false);
                      handleHomeClick(item.value);
                    }}
                    activeClassName="bg-brand-light text-brand-secondary"
                    className={cn(
                      "text-brand hover:bg-brand-light hover:text-brand-secondary group flex items-center px-4 py-[1cqh] text-base font-normal rounded-md",
                      { "mt-auto": item.path.includes("refer-friend") },
                    )}
                  >
                    {item.Icon && (
                      <div className="relative">
                        {hasNewNotifications && item.name === "Home" && (
                          <NewNotificationIcon className="absolute -top-1 -left-2" />
                        )}

                        {hasNewMaterials && item.name === "Materials" && (
                          <NewNotificationIcon className="absolute -top-1 -left-2" />
                        )}

                        {hasNewExercises && item.name === "Exercises" && (
                          <NewNotificationIcon className="absolute -top-1 -left-2" />
                        )}

                        {isDiplomaRequestUnlock &&
                          item.name === "Diploma request" && (
                            <NewNotificationIcon className="absolute -top-1 -left-2" />
                          )}

                        <item.Icon
                          className="group-[.text-brand-secondary]:text-brand-secondary group-hover:text-brand-secondary mr-5 flex-shrink-0 h-6 w-6"
                          aria-hidden="true"
                        />
                      </div>
                    )}

                    <span className="inline-flex flex-grow justify-between items-center">
                      <span>{item.value}</span>
                    </span>
                  </ActiveLink>
                ),
              )}

              <div className="flex gap-1 items-center px-4">
                <LogoutIcon className="h-6 w-6 fill-brand-secondary" />

                <Button
                  data-track-props={mixpanelPropsData}
                  data-track-eventtype={
                    MIXPANEL_EVENT_TYPES.SIDEBAR_MENU_LOGOUT
                  }
                  data-track-pageversion={MIXPANEL_PAGE_VERSION_MOBILE}
                  variant="link"
                  onClick={() => logout()}
                >
                  {t("menu.logout")}
                </Button>
              </div>
            </nav>
          </ScrollArea>
        </div>
      </nav>
      {/*End of Mobile Nav*/}

      {/*Desktop Sidebar*/}
      <div className=" flex until-md:hidden w-64 md:flex-col @container">
        <div className="flex h-full flex-1 flex-col border-r border-brand-black-secondary bg-light">
          <div className="flex flex-1 flex-col overflow-y-auto  pb-4">
            <div className="mr-auto pl-[--dashboard-logo-padding] pt-[--page-padding-v-lg]">
              <Link to={routes.home}>
                {!isItalian ? (
                  <EasyPeasyLogo height={47.86} color="#2D224C" />
                ) : (
                  <FullLogo width={112} className="fill-brand" />
                )}
              </Link>
            </div>

            <nav className="mt-5 flex-1 gap-1 flex flex-col h-full px-[--dashboard-sidebar]">
              {sidebarContent
                ? sidebarContent
                : menuItems.map((item) =>
                    (me?.item.user.english_level == "A0" &&
                      item.value == "1:1 classes") ||
                    (!isIeltsCourse && item.value == "Book exam") ||
                    (item.value == "Availability" &&
                      menuItemsToHide.includes(item.value)) ||
                    (item.value == "Availability" &&
                      isItalianNoDeposit) ? null : (
                      <ActiveLink
                        data-track-props={mixpanelPropsData}
                        data-track-eventtype={getSidebarEventName(item.value)}
                        key={item.value}
                        to={item.path}
                        onClick={() => {
                          handleHomeClick(item.value);
                        }}
                        activeClassName="bg-brand-light text-brand-secondary"
                        className={cn(
                          "text-brand hover:bg-brand-light hover:text-brand-secondary group flex items-center px-4 py-[1cqh] text-base font-normal rounded-md",
                        )}
                      >
                        {item.Icon && (
                          <div className="relative">
                            {hasNewNotifications && item.name === "Home" && (
                              <NewNotificationIcon className="absolute -top-1 -left-2" />
                            )}

                            {hasNewMaterials && item.name === "Materials" && (
                              <NewNotificationIcon className="absolute -top-1 -left-2" />
                            )}

                            {hasNewExercises && item.name === "Exercises" && (
                              <NewNotificationIcon className="absolute -top-1 -left-2" />
                            )}

                            {isDiplomaRequestUnlock &&
                              item.name === "Diploma request" && (
                                <NewNotificationIcon className="absolute -top-1 -left-2" />
                              )}

                            <item.Icon
                              className="group-[.text-brand-secondary]:text-brand-secondary group-hover:text-brand-secondary mr-5 flex-shrink-0 h-6 w-6"
                              aria-hidden="true"
                            />
                          </div>
                        )}

                        <span
                          data-track-props={mixpanelPropsData}
                          data-track-eventtype={getSidebarEventName(item.value)}
                          className="inline-flex flex-grow justify-between items-center"
                        >
                          <span
                            data-track-props={mixpanelPropsData}
                            data-track-eventtype={getSidebarEventName(
                              item.value,
                            )}
                          >
                            {item.value}
                          </span>
                        </span>
                      </ActiveLink>
                    ),
                  )}

              {!sidebarContent && (
                <div className="text-brand mb-5 hover:bg-brand-light hover:text-brand-secondary group flex items-center px-4 text-base font-normal rounded-md">
                  <LogoutIcon className="h-6 w-6 fill-brand-secondary" />

                  <Button
                    data-track-props={mixpanelPropsData}
                    data-track-eventtype={
                      MIXPANEL_EVENT_TYPES.SIDEBAR_MENU_LOGOUT
                    }
                    variant="link"
                    className="!no-underline text-brand-secondary text-base font-normal"
                    onClick={() => logout()}
                  >
                    {t("menu.logout")}
                  </Button>
                </div>
              )}
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
